import { DragItem, HierarchyFolder, HomePageFilterState } from 'app/home/index.functions';
import useBundleTranslation from 'i18n';
import { useNavigate } from 'react-router-dom';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import React, { useState, useEffect } from 'react';
import styles from 'app/home/hierarchy/HierarchyPanel.styles';
import { getEditUrl } from 'tools/tools';
import { useDrop } from 'react-dnd';
import { addElementToFolder, addElementToFavorite } from 'app/home/hierarchy/HierarchyPanel.functions';
import { useHomeContext } from 'app/home/context';
import { Box, Stack } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import ControlledDropdownIcon from 'app/home/hierarchy/ControlledDropdownIcon';
import { homePage } from 'api/home-page';
import { useHierarchyMenu } from 'app/home/hierarchy/HierarchyMenuContext';
import toast from 'app/toast';

export default function HierarchyItem({
    h,
    level,
    type,
    addFolder,
    setIsFolderItemMenuActive = () => {},
}: {
    h: HierarchyFolder;
    level: number;
    type: string;
    addFolder?: (parentFolderId: number, parentFolderName: string) => void;
    setIsFolderItemMenuActive?: (isActive: boolean) => void;
}) {
    const { t } = useBundleTranslation(['app/home']);
    const ctx = useHomeContext();
    const homePageState = ctx.homePageState;
    const setHomePageState = ctx.setHomePageState;
    const refetchData = ctx.refetchData;
    const setData = ctx.setData;
    const data = ctx.data;
    const tiles = ctx.tiles;

    const [isCurrentFolderItemMenuActive, setIsCurrentFolderItemMenuActive] = useState<boolean>(false);
    const { openMenuId } = useHierarchyMenu();
    const menuId = `folder-item-${h.folder_id}`;

    const navigate = useNavigate();
    const userAuth: UserAuth = useAppSelector((state) => state.auth);

    useEffect(() => {
        if (openMenuId !== null && openMenuId !== menuId && isCurrentFolderItemMenuActive) {
            setIsCurrentFolderItemMenuActive(false);
            setIsFolderItemMenuActive(false);
        }
    }, [openMenuId, menuId, isCurrentFolderItemMenuActive]);

    const [collectedProps, drop] = useDrop(() => ({
        accept: 'tile',
        drop: async (item: DragItem, monitor) => {
            if (h.folder_id ?? 0 > 0) {
                if (item.tile.folder_id?.includes(h.folder_id ?? 0)) {
                    toast('info', t('info'), t('exists_folder'), { duration: 5000 });
                    return;
                }
                let newItem = await addElementToFolder(h.folder_id ?? 0, item.tile, t);
                if (!newItem) {
                    return;
                }
                setData({ ...data, folderItems: [...data.folderItems, newItem] });
            } else if (h.favorite_id ?? 0 > 0) {
                let tile = tiles.find(
                    (t) =>
                        t.element_id == item.tile.element_id &&
                        t.segment_value_id == item.tile.segment_value_id &&
                        t.favorite_id == h.favorite_id,
                );
                if (tile) {
                    toast('info', t('info'), t('exists_favorite'), { duration: 5000 });
                } else {
                    await addElementToFavorite(h.favorite_id ?? 0, item.tile, t);
                    refetchData();
                }
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    }));

    let ref = React.useRef<HTMLDivElement>(null);

    let k = h.k || '';
    let expanded = homePageState && homePageState.expandedFolders && homePageState.expandedFolders[k];
    let hasChildren = h.children && h.children.length > 0;
    const onClick = () => {
        let state = { ...homePageState };
        if (undefined === state.expandedFolders) state.expandedFolders = {};
        if (undefined === state.expandedFolders[k]) state.expandedFolders[k] = true;
        else state.expandedFolders[k] = !state.expandedFolders[k];

        setHomePageState(state);
        return false;
    };

    const editFolderClick = (folderId: number) => {
        navigate(getEditUrl('folder') + folderId);
    };

    const setFilterState = (filterState: Partial<HomePageFilterState>) => {
        setHomePageState({ ...homePageState, filterState: { ...homePageState.filterState, ...filterState } });
    };

    const childIndent = String(level * 24) + 'px';
    const isActiveItem = homePageState.filterState.folderSelected == h.k;

    if (
        ((h.folder_id ?? 0) > 0 && (userAuth.userInfo?.isAdmin || 'Y' == h.is_edit_access_ind)) ||
        (h.favorite_id ?? 0) > 0
    ) {
        drop(ref);
    }

    const itemId =
        type === 'folders'
            ? h.folder_id
            : type === 'categories'
              ? h.category_id
              : type === 'favorites'
                ? h.favorite_id
                : 0;

    const addSubFolder = () => {
        const folderId = h.folder_id ?? 0;
        const folderName = h.name;

        if (addFolder && folderId > 0) {
            addFolder(folderId, folderName);
        }
    };

    const addToNewBurst = () => {
        homePage.addNewBurst(h.folder_id ?? 0).then((response) => {
            if (response.redirectUrl) navigate(response.redirectUrl);
        });
    };

    return (
        <Box>
            <Stack
                sx={styles.hierarchyItem}
                className={`hierarchyItem ${isActiveItem ? 'active' : ''} ${collectedProps.isOver ? 'drag-over' : ''} ${isCurrentFolderItemMenuActive ? 'item-action-menu-active' : ''}`}
                data-key={h.k}
                ref={ref}
                data-test={`homepage-tree-${type}-${itemId}`}
            >
                <Box
                    sx={{ ...styles.hierarchyItemChildLine, width: childIndent }}
                    className={'hierarchy-item__child-line'}
                />
                <Box sx={styles.hierarchyItemExpandIcon} className={'hierarchy-item__expand-icon'}>
                    {hasChildren && (
                        <Box onClick={onClick} sx={styles.hierarchyItemExpandIconHolder}>
                            <IconMi icon={expanded ? 'menu-opened' : 'menu-closed'} sx={{ fontSize: '9px' }} />
                        </Box>
                    )}
                </Box>
                <Box
                    onClick={() => {
                        setFilterState({
                            folderSelected: h.k,
                            lastFilterApplied: 'folderSelected',
                        });
                    }}
                    sx={styles.hierarchyItemLabel}
                    className={'hierarchy-item__label'}
                >
                    <Box sx={styles.hierarchyItemLabelAlign}>{h.name}</Box>
                </Box>
                {(h.folder_id ?? 0) > 0 && (userAuth.userInfo?.isAdmin || 'Y' == h.is_edit_access_ind) && (
                    <Box sx={styles.hierarchyItemActionMenuBtn} className={'item-action-menu'}>
                        <ControlledDropdownIcon
                            icon="more"
                            popperSx={styles.hierarchyActionMenuHolder}
                            placement={'bottom-end'}
                            menuId={`folder-item-${h.folder_id}`}
                            onChangeState={(isOpen) => {
                                setIsCurrentFolderItemMenuActive(isOpen);
                                if (isCurrentFolderItemMenuActive && !isOpen) {
                                    setIsFolderItemMenuActive(false);
                                }
                                if (!isCurrentFolderItemMenuActive && isOpen) {
                                    setTimeout(() => {
                                        setIsFolderItemMenuActive(true);
                                    }, 10);
                                }
                            }}
                        >
                            <Box sx={styles.hierarchyActionMenuList}>
                                <Box
                                    sx={styles.hierarchyActionMenuItem}
                                    onClick={() => editFolderClick(h.folder_id ?? 0)}
                                >
                                    <Box sx={styles.hierarchyActionMenuItemIcon}>
                                        <IconMi icon={'gear'} fontSize={'16'} />
                                    </Box>
                                    <Box sx={styles.hierarchyActionMenuItemLabel}>{t('edit_folder')}</Box>
                                </Box>
                                <Box sx={styles.hierarchyActionMenuItem} onClick={addSubFolder}>
                                    <Box sx={styles.hierarchyActionMenuItemIcon}>
                                        <IconMi icon={'new'} fontSize={'16'} />
                                    </Box>
                                    <Box sx={styles.hierarchyActionMenuItemLabel}>{t('add_subfolder')}</Box>
                                </Box>
                                <Box sx={styles.hierarchyActionMenuItem} onClick={addToNewBurst}>
                                    <Box sx={styles.hierarchyActionMenuItemIcon}>
                                        <IconMi icon={'burst'} fontSize={'16'} />
                                    </Box>
                                    <Box sx={styles.hierarchyActionMenuItemLabel}>{t('include_in_new_burst')}</Box>
                                </Box>
                            </Box>
                        </ControlledDropdownIcon>
                    </Box>
                )}
            </Stack>
            {expanded &&
                h.children &&
                h.children.length > 0 &&
                h.children.map((c, key) => (
                    <HierarchyItem h={c} key={c.k} level={level + 1} type={type} addFolder={addFolder} />
                ))}
        </Box>
    );
}
